import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Upload, FileText, Image, CheckCircle, ArrowLeft, ArrowRight,
  Loader2, AlertCircle, Edit2, Trash2, Plus, Save, Eye, X,
  BookOpen, Layers, Target, Download
} from 'lucide-react';

const PDFImportWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Session data
  const [sessionId, setSessionId] = useState(null);
  const [importedSessionId, setImportedSessionId] = useState(null); // Track last imported session
  const [paperInfo, setPaperInfo] = useState({
    board: 'Cambridge IGCSE',
    subject: '',
    paper_code: '',
    year: '',
    session: '',
    variant: '',
    total_marks: 80
  });
  const [questions, setQuestions] = useState([]);
  const [figuresFound, setFiguresFound] = useState([]);
  const [tablesFound, setTablesFound] = useState([]);
  const [extractedImages, setExtractedImages] = useState([]);
  const [uploadedFigures, setUploadedFigures] = useState({});
  const [figureToQuestionMap, setFigureToQuestionMap] = useState({}); // Maps figure name to question numbers
  
  // Import settings
  const [importSettings, setImportSettings] = useState({
    board: 'Cambridge IGCSE',
    subject: '',
    paper: '',
    topic: '',
    subtopic: '',
    difficulty: 'medium'
  });
  
  // Editing state
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Mark Scheme state
  const [markSchemeFile, setMarkSchemeFile] = useState(null);
  const [markSchemeSessionId, setMarkSchemeSessionId] = useState(null);
  const [markingSchemes, setMarkingSchemes] = useState([]);
  const [loadingMarkScheme, setLoadingMarkScheme] = useState(false);

  // Step 1: Upload PDF
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/pdf-import/upload-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSessionId(response.data.session_id);
      setPaperInfo(response.data.paper_info);
      setQuestions(response.data.questions || []);
      setFiguresFound(response.data.figures_found || []);
      setTablesFound(response.data.tables_found || []);
      setExtractedImages(response.data.images || []);
      
      // Build figure-to-question mapping
      const figureMap = {};
      (response.data.questions || []).forEach(q => {
        if (q.figure_reference) {
          const figName = q.figure_reference;
          if (!figureMap[figName]) {
            figureMap[figName] = [];
          }
          const qLabel = `Q${q.question_number}${q.part_label ? `(${q.part_label})` : ''}${q.sub_part_label ? `(${q.sub_part_label})` : ''}`;
          if (!figureMap[figName].includes(qLabel)) {
            figureMap[figName].push(qLabel);
          }
        }
      });
      setFigureToQuestionMap(figureMap);
      
      // Pre-fill import settings
      setImportSettings(prev => ({
        ...prev,
        board: response.data.paper_info.board || 'Cambridge IGCSE',
        subject: response.data.paper_info.subject || '',
        paper: response.data.paper_info.paper_code || ''
      }));
      
      const tablesCount = response.data.tables_count || 0;
      toast.success(`PDF parsed! Found ${response.data.questions_count} questions${tablesCount > 0 ? ` and ${tablesCount} tables` : ''}`);
      setStep(2);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setLoading(false);
    }
  };

  // Upload Mark Scheme PDF
  const handleMarkSchemeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    
    setMarkSchemeFile(file);
    setLoadingMarkScheme(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('session_id', sessionId); // Link to the question paper session
    
    try {
      const response = await api.post('/pdf-import/upload-mark-scheme', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setMarkSchemeSessionId(response.data.session_id);
      setMarkingSchemes(response.data.marking_schemes || []);
      
      // Auto-link marking schemes to questions
      const linkedCount = linkMarkingSchemesToQuestions(response.data.marking_schemes || []);
      
      toast.success(`Mark scheme parsed! Found ${response.data.schemes_count} marking schemes. ${linkedCount} linked to questions.`);
    } catch (err) {
      console.error('Mark scheme upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload mark scheme');
    } finally {
      setLoadingMarkScheme(false);
    }
  };

  // Link marking schemes to questions based on question number, part, and subpart
  const linkMarkingSchemesToQuestions = (schemes) => {
    let linkedCount = 0;
    
    setQuestions(prevQuestions => {
      const updatedQuestions = prevQuestions.map(q => {
        // Find matching marking scheme
        const matchingScheme = schemes.find(ms => 
          ms.question_number === q.question_number &&
          ms.part_label === q.part_label &&
          ms.sub_part_label === q.sub_part_label
        );
        
        if (matchingScheme) {
          linkedCount++;
          return {
            ...q,
            marking_scheme: matchingScheme.marking_points,
            model_answer: matchingScheme.model_answer || q.model_answer
          };
        }
        return q;
      });
      return updatedQuestions;
    });
    
    return linkedCount;
  };

  // Step 2: Edit question
  const handleQuestionEdit = (index, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDeleteQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
    toast.success('Question removed');
  };

  const handleAddQuestion = () => {
    const lastQ = questions[questions.length - 1];
    const newQ = {
      question_number: lastQ ? lastQ.question_number : 1,
      part_label: 'a',
      sub_part_label: 'i',
      question_text: '',
      marks: 1,
      figure_reference: null,
      requires_diagram: false,
      assessment_objective: 'AO2'
    };
    setQuestions(prev => [...prev, newQ]);
  };

  // Step 3: Upload images
  const handleImageUpload = async (figureName, file) => {
    if (!sessionId) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('figure_name', figureName);
    
    try {
      const response = await api.post(`/pdf-import/upload-image/${sessionId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedFigures(prev => ({
        ...prev,
        [figureName]: response.data.filename
      }));
      
      toast.success(`Image uploaded for ${figureName}`);
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Step 4: Import to Question Bank
  const handleImport = async () => {
    if (!sessionId) {
      toast.error('No session found');
      return;
    }
    
    if (questions.length === 0) {
      toast.error('No questions to import');
      return;
    }
    
    if (!importSettings.subject || !importSettings.paper) {
      toast.error('Please fill in Subject and Paper');
      return;
    }

    if (!paperInfo.year || !paperInfo.session || !paperInfo.variant) {
      toast.error('Please fill in Year, Session, and Variant');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/pdf-import/import', {
        session_id: sessionId,
        ...importSettings,
        year: paperInfo.year,
        session: paperInfo.session,
        variant: paperInfo.variant,
        questions: questions
      });
      
      // Store the session ID for potential deletion
      setImportedSessionId(sessionId);
      toast.success(`Imported ${response.data.imported_count} questions!`);
      setStep(5); // Go to success/completion step
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'Failed to import questions');
    } finally {
      setLoading(false);
    }
  };

  // Delete imported questions from current session
  const handleDeleteImported = async () => {
    if (!importedSessionId) {
      toast.error('No import session to delete');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete all questions imported in this session? This cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.delete(`/pdf-import/imported/${importedSessionId}`);
      toast.success(response.data.message || 'Deleted imported questions');
      setImportedSessionId(null);
      // Reset to step 1 for new import
      setStep(1);
      setSessionId(null);
      setQuestions([]);
      setFiguresFound([]);
      setTablesFound([]);
      setUploadedFigures({});
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete questions');
    } finally {
      setLoading(false);
    }
  };

  // Calculate question totals
  const getQuestionSummary = () => {
    const summary = {};
    questions.forEach(q => {
      const qNum = q.question_number;
      if (!summary[qNum]) {
        summary[qNum] = { totalMarks: 0, parts: [] };
      }
      summary[qNum].totalMarks += (q.marks || 0);
      summary[qNum].parts.push({
        part: q.part_label,
        subPart: q.sub_part_label,
        marks: q.marks || 0
      });
    });
    return summary;
  };

  const getAOColor = (ao) => {
    switch(ao) {
      case 'AO1': return 'bg-blue-100 text-blue-700';
      case 'AO2': return 'bg-green-100 text-green-700';
      case 'AO3': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <button 
            onClick={() => navigate('/qbm/dashboard')}
            className="flex items-center gap-2 text-emerald-200 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Upload className="w-7 h-7" />
            Import Exam Paper
          </h1>
          <p className="mt-1 text-emerald-200">Upload IGCSE exam papers and import questions to the Question Bank</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center mb-8">
          {[
            { num: 1, label: 'Upload PDF', icon: FileText },
            { num: 2, label: 'Review Questions', icon: Edit2 },
            { num: 3, label: 'Upload Images', icon: Image },
            { num: 4, label: 'Import', icon: CheckCircle },
            { num: 5, label: 'Complete', icon: CheckCircle }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  step === s.num 
                    ? 'bg-emerald-600 text-white' 
                    : step > s.num 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-200 text-slate-500'
                }`}
              >
                <s.icon className="w-5 h-5" />
                <span className="font-medium">{s.label}</span>
              </div>
              {idx < 4 && (
                <div className={`w-12 h-1 mx-2 ${step > s.num ? 'bg-emerald-400' : 'bg-slate-300'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          
          {/* Step 1: Upload PDF */}
          {step === 1 && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <FileText className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Upload Exam Paper</h2>
                <p className="text-slate-600 mb-6">
                  Upload an IGCSE exam paper PDF. The system will extract and parse questions automatically.
                </p>
                
                <label className="block">
                  <div className={`border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all ${
                    loading ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50'
                  }`}>
                    {loading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-2" />
                        <p className="text-emerald-700">Parsing PDF...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-600">Click to upload or drag and drop</p>
                        <p className="text-sm text-slate-400 mt-1">PDF files only</p>
                      </>
                    )}
                  </div>
                  <input 
                    type="file" 
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={loading}
                  />
                </label>
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left">
                  <h3 className="font-medium text-blue-900 mb-2">Supported Papers:</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Cambridge IGCSE Physics, Chemistry, Biology</li>
                    <li>• Paper 2, Paper 4, Paper 6 (Extended)</li>
                    <li>• Multiple choice and structured questions</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Review Questions */}
          {step === 2 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Review Extracted Questions</h2>
                  <p className="text-slate-600">
                    {questions.length} questions found. Edit or remove as needed.
                  </p>
                </div>
                <button
                  onClick={handleAddQuestion}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>

              {/* Paper Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-xs text-slate-500">Board</label>
                  <p className="font-medium">{paperInfo.board}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Subject</label>
                  <p className="font-medium">{paperInfo.subject || 'Not detected'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Paper Code</label>
                  <p className="font-medium">{paperInfo.paper_code || 'Not detected'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Session / Year / Variant</label>
                  <p className="font-medium">{paperInfo.session} {paperInfo.year} {paperInfo.variant ? `V${paperInfo.variant}` : ''}</p>
                </div>
              </div>

              {/* Mark Scheme Upload */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-medium text-amber-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Mark Scheme (Optional)
                </h3>
                {!markSchemeFile ? (
                  <div className="flex items-center gap-4">
                    <label className="flex-1 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-100 transition-all">
                      <Upload className="w-5 h-5 text-amber-600" />
                      <span className="text-amber-700">Upload Mark Scheme PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleMarkSchemeUpload}
                        className="hidden"
                        data-testid="mark-scheme-upload"
                      />
                    </label>
                    <p className="text-sm text-amber-700 max-w-xs">
                      Upload the official IGCSE mark scheme PDF to auto-populate marking points for AI grading.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {loadingMarkScheme ? (
                        <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                      <div>
                        <p className="font-medium text-amber-900">{markSchemeFile.name}</p>
                        <p className="text-sm text-amber-700">
                          {loadingMarkScheme 
                            ? 'Parsing mark scheme...' 
                            : `${markingSchemes.length} marking schemes extracted`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setMarkSchemeFile(null);
                        setMarkSchemeSessionId(null);
                        setMarkingSchemes([]);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {markingSchemes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <p className="text-sm text-amber-700">
                      <CheckCircle className="w-4 h-4 inline mr-1 text-green-600" />
                      Marking schemes linked to questions. They will be saved with the Question Bank import.
                    </p>
                  </div>
                )}
              </div>

              {/* Extracted Tables */}
              {tablesFound.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Tables Extracted from PDF ({tablesFound.length})
                  </h3>
                  <div className="space-y-4">
                    {tablesFound.map((table, idx) => (
                      <div key={idx} className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                        <div className="px-3 py-2 bg-blue-100 text-sm font-medium text-blue-800">
                          Page {table.page} - Table {table.table_index + 1} ({table.rows} rows)
                        </div>
                        <div className="overflow-x-auto max-h-40">
                          <table className="w-full text-xs">
                            <tbody>
                              {table.data?.slice(0, 5).map((row, rowIdx) => (
                                <tr key={rowIdx} className={rowIdx === 0 ? 'bg-slate-100 font-medium' : ''}>
                                  {row.map((cell, cellIdx) => (
                                    <td key={cellIdx} className="px-2 py-1 border-b border-r border-slate-200">
                                      {cell || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                              {table.data?.length > 5 && (
                                <tr>
                                  <td colSpan={table.data[0]?.length || 1} className="px-2 py-1 text-center text-slate-500 italic">
                                    ... {table.data.length - 5} more rows
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-blue-700">
                    Tables are extracted and can be referenced in questions. You may need to manually associate them with specific questions.
                  </p>
                </div>
              )}

              {/* Question Marks Summary */}
              {questions.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h3 className="font-medium text-amber-900 mb-3">Marks Summary by Question</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(getQuestionSummary()).map(([qNum, data]) => (
                      <div key={qNum} className="px-3 py-2 bg-white rounded-lg border border-amber-200">
                        <span className="font-bold text-amber-800">Q{qNum}</span>
                        <span className="ml-2 text-amber-600">{data.totalMarks} marks</span>
                        <span className="ml-1 text-xs text-amber-500">({data.parts.length} parts)</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-amber-700">
                    Total: <strong>{questions.reduce((sum, q) => sum + (q.marks || 0), 0)} marks</strong> across {Object.keys(getQuestionSummary()).length} questions
                  </p>
                </div>
              )}

              {/* Questions List */}
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {questions.map((q, idx) => (
                  <div 
                    key={idx}
                    className="p-4 border border-slate-200 rounded-lg hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-sm font-medium">
                            Q{q.question_number}
                            {q.part_label && `(${q.part_label})`}
                            {q.sub_part_label && `(${q.sub_part_label})`}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getAOColor(q.assessment_objective)}`}>
                            {q.assessment_objective}
                          </span>
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                            [{q.marks} marks]
                          </span>
                          {q.requires_diagram && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              Diagram
                            </span>
                          )}
                          {q.figure_reference && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                              {q.figure_reference}
                            </span>
                          )}
                        </div>
                        
                        {editingQuestion === idx ? (
                          <div className="space-y-3">
                            <textarea
                              value={q.question_text}
                              onChange={(e) => handleQuestionEdit(idx, 'question_text', e.target.value)}
                              className="w-full p-2 border rounded-lg text-sm h-24"
                            />
                            <div className="grid grid-cols-4 gap-2">
                              <div>
                                <label className="text-xs text-slate-500">Q#</label>
                                <input
                                  type="number"
                                  value={q.question_number}
                                  onChange={(e) => handleQuestionEdit(idx, 'question_number', parseInt(e.target.value))}
                                  className="w-full p-1 border rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Part</label>
                                <input
                                  type="text"
                                  value={q.part_label || ''}
                                  onChange={(e) => handleQuestionEdit(idx, 'part_label', e.target.value)}
                                  className="w-full p-1 border rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Sub-part</label>
                                <input
                                  type="text"
                                  value={q.sub_part_label || ''}
                                  onChange={(e) => handleQuestionEdit(idx, 'sub_part_label', e.target.value)}
                                  className="w-full p-1 border rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Marks</label>
                                <input
                                  type="number"
                                  value={q.marks}
                                  onChange={(e) => handleQuestionEdit(idx, 'marks', parseInt(e.target.value))}
                                  className="w-full p-1 border rounded text-sm"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-xs text-slate-500">AO</label>
                                <select
                                  value={q.assessment_objective}
                                  onChange={(e) => handleQuestionEdit(idx, 'assessment_objective', e.target.value)}
                                  className="w-full p-1 border rounded text-sm"
                                >
                                  <option value="AO1">AO1</option>
                                  <option value="AO2">AO2</option>
                                  <option value="AO3">AO3</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Figure Ref</label>
                                <input
                                  type="text"
                                  value={q.figure_reference || ''}
                                  onChange={(e) => handleQuestionEdit(idx, 'figure_reference', e.target.value)}
                                  className="w-full p-1 border rounded text-sm"
                                  placeholder="e.g., Fig. 1.1"
                                />
                              </div>
                              <div className="flex items-end">
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={q.requires_diagram}
                                    onChange={(e) => handleQuestionEdit(idx, 'requires_diagram', e.target.checked)}
                                  />
                                  Requires Drawing
                                </label>
                              </div>
                            </div>
                            <button
                              onClick={() => setEditingQuestion(null)}
                              className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
                            >
                              Done
                            </button>
                          </div>
                        ) : (
                          <p className="text-slate-700 text-sm">{q.question_text}</p>
                        )}
                        
                        {/* Show marking scheme if present */}
                        {q.marking_scheme && (
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                            <span className="font-medium text-amber-800">Marking Scheme: </span>
                            <span className="text-amber-700">{q.marking_scheme}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingQuestion(editingQuestion === idx ? null : idx)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(idx)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-6 pt-6 border-t">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Next: Upload Images
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Upload Images */}
          {step === 3 && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900">Upload Figure Images <span className="text-sm font-normal text-slate-500">(Optional)</span></h2>
                <p className="text-slate-600">
                  You can upload images now or skip this step and add diagrams later using Edit Question.
                </p>
              </div>

              {/* Important notice */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">This step is optional</h3>
                <p className="text-sm text-blue-700 mb-3">
                  You can skip this step and add diagrams later using the <strong>Edit Question</strong> button 
                  in the Question Bank. This is often easier as you can see each question and attach the right diagram.
                </p>
                <details className="text-sm text-blue-700">
                  <summary className="cursor-pointer font-medium">How to upload figures here (optional)</summary>
                  <ul className="mt-2 space-y-1 ml-4">
                    <li>1. Open the PDF in a viewer (Adobe Reader, Chrome, etc.)</li>
                    <li>2. Use the screenshot/snipping tool to capture each figure</li>
                    <li>3. Save as PNG or JPG and upload below</li>
                    <li>4. Or use "Save Image As" if available in your PDF viewer</li>
                  </ul>
                </details>
              </div>

              {/* Figure references to upload */}
              <div className="mb-6">
                <h3 className="font-medium text-slate-900 mb-3">
                  Figures Referenced in Questions ({figuresFound.length} found)
                </h3>
                {figuresFound.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {figuresFound.map((fig, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          uploadedFigures[fig] 
                            ? 'border-emerald-300 bg-emerald-50' 
                            : 'border-slate-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{fig}</span>
                              {uploadedFigures[fig] && (
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              )}
                            </div>
                            {/* Show which questions reference this figure */}
                            {figureToQuestionMap[fig] && figureToQuestionMap[fig].length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                <span className="text-xs text-slate-500">Used in:</span>
                                {figureToQuestionMap[fig].map((qLabel, i) => (
                                  <span key={i} className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                                    {qLabel}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <label className="cursor-pointer flex-shrink-0">
                            <div className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              uploadedFigures[fig]
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-emerald-600 text-white hover:bg-emerald-700'
                            }`}>
                              {uploading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : uploadedFigures[fig] ? (
                                'Replace'
                              ) : (
                                'Upload'
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleImageUpload(fig, e.target.files[0]);
                                }
                              }}
                              className="hidden"
                              disabled={uploading}
                            />
                          </label>
                        </div>
                        {/* Preview uploaded image */}
                        {uploadedFigures[fig] ? (
                          <div className="relative">
                            <img 
                              src={uploadedFigures[fig]} 
                              alt={fig}
                              className="w-full h-32 object-contain bg-white rounded border"
                            />
                            <button
                              onClick={() => setUploadedFigures(prev => {
                                const updated = {...prev};
                                delete updated[fig];
                                return updated;
                              })}
                              className="absolute top-1 right-1 p-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                              title="Remove image"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="h-24 flex items-center justify-center bg-slate-100 rounded border-2 border-dashed border-slate-300">
                            <div className="text-center text-slate-400">
                              <Image className="w-8 h-8 mx-auto mb-1" />
                              <span className="text-xs">Click Upload to add image</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500 bg-slate-50 rounded-lg">
                    <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No figure references found in the parsed questions.</p>
                    <p className="text-sm">You can add images manually below or skip this step.</p>
                  </div>
                )}
              </div>

              {/* Upload progress summary */}
              {figuresFound.length > 0 && (
                <div className="mb-6 p-4 bg-slate-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Upload Progress:</span>
                    <span className="text-sm font-medium">
                      {Object.keys(uploadedFigures).length} / {figuresFound.length} figures uploaded
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${(Object.keys(uploadedFigures).length / figuresFound.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Manual upload */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-900 mb-2">Upload Additional Images:</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Figure name (e.g., Fig. 2.1)"
                    id="manual-figure-name"
                    className="flex-1 p-2 border rounded-lg text-sm"
                  />
                  <label className="cursor-pointer">
                    <div className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-300">
                      Select Image
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const nameInput = document.getElementById('manual-figure-name');
                        if (e.target.files[0] && nameInput.value) {
                          handleImageUpload(nameInput.value, e.target.files[0]);
                          nameInput.value = '';
                        } else if (!nameInput.value) {
                          toast.error('Please enter a figure name first');
                        }
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-6 pt-6 border-t">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-500">
                    You can add diagrams later using Edit Question
                  </span>
                  <button
                    onClick={() => setStep(4)}
                    className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    {Object.keys(uploadedFigures).length === 0 ? 'Skip Images & Continue' : 'Next: Review & Import'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Import Settings & Confirm */}
          {step === 4 && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900">Import Settings</h2>
                <p className="text-slate-600">
                  Configure how questions will be organized in the Question Bank.
                </p>
              </div>

              {/* Import Settings Form */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Board <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={importSettings.board}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, board: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="Cambridge IGCSE">Cambridge IGCSE</option>
                    <option value="Edexcel IGCSE">Edexcel IGCSE</option>
                    <option value="AQA">AQA</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={importSettings.subject}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., Physics"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Paper <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={importSettings.paper}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, paper: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., Paper 4 or 0625/43"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={paperInfo.year || ''}
                    onChange={(e) => setPaperInfo(prev => ({ ...prev, year: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Session <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={paperInfo.session || ''}
                    onChange={(e) => setPaperInfo(prev => ({ ...prev, session: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">Select Session</option>
                    <option value="May/June">May/June</option>
                    <option value="Oct/Nov">Oct/Nov</option>
                    <option value="Feb/Mar">Feb/Mar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Variant <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={paperInfo.variant || ''}
                    onChange={(e) => setPaperInfo(prev => ({ ...prev, variant: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">Select Variant</option>
                    <option value="1">Variant 1 (e.g., /41)</option>
                    <option value="2">Variant 2 (e.g., /42)</option>
                    <option value="3">Variant 3 (e.g., /43)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    value={importSettings.difficulty}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Topic (Optional)
                  </label>
                  <input
                    type="text"
                    value={importSettings.topic}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, topic: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., Forces and Motion"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subtopic (Optional)
                  </label>
                  <input
                    type="text"
                    value={importSettings.subtopic}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, subtopic: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., Newton's Laws"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="p-6 bg-emerald-50 rounded-xl mb-6">
                <h3 className="font-semibold text-emerald-900 mb-4">Import Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-emerald-600">Questions</p>
                    <p className="text-2xl font-bold text-emerald-900">{questions.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-600">Total Marks</p>
                    <p className="text-2xl font-bold text-emerald-900">
                      {questions.reduce((sum, q) => sum + (q.marks || 0), 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-600">With Diagrams</p>
                    <p className="text-2xl font-bold text-emerald-900">
                      {questions.filter(q => q.requires_diagram).length}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-600">Images Uploaded</p>
                    <p className="text-2xl font-bold text-emerald-900">
                      {Object.keys(uploadedFigures).length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={loading}
                  className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Import to Question Bank
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Import Complete */}
          {step === 5 && (
            <div className="text-center py-12">
              <div className="max-w-lg mx-auto">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Import Successful!</h2>
                <p className="text-slate-600 mb-8">
                  Your questions have been imported to the Question Bank successfully.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/qbm/dashboard')}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    <BookOpen className="w-5 h-5" />
                    Go to Dashboard
                  </button>
                  
                  <button
                    onClick={() => {
                      setStep(1);
                      setSessionId(null);
                      setImportedSessionId(null);
                      setQuestions([]);
                      setFiguresFound([]);
                      setTablesFound([]);
                      setUploadedFigures({});
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                  >
                    <Plus className="w-5 h-5" />
                    Import Another PDF
                  </button>
                </div>

                {importedSessionId && (
                  <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm mb-3">
                      Made a mistake? You can delete all questions from this import session.
                    </p>
                    <button
                      onClick={handleDeleteImported}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 mx-auto"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete Imported Questions
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFImportWizard;
